import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

import * as authApi from '../api/auth';
import { extractErrorMessage } from '../api/errors';
import { PasswordRulesHint } from '../components/PasswordRulesHint';
import {
  LegalConsentCheckbox,
  isLegalConflict,
  useLegalConsent,
} from '../components/legal/LegalConsent';
import { validatePassword } from '../validation/password';

/**
 * Self-registration page. On submit, fires `POST /auth/register`
 * which queues a verification email. We always show the same
 * success state regardless of whether the email was actually new,
 * so an attacker can't enumerate users.
 */
export function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const consent = useLegalConsent();

  const passwordError = password.length > 0 ? validatePassword(password) : null;
  const canSubmit =
    !submitting && validatePassword(password) === null && consent.ready;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.register({
        email,
        password,
        full_name: fullName.trim(),
        legal_document_ids: consent.documentIds,
      });
      setDone(true);
    } catch (err: unknown) {
      if (isLegalConflict(err)) {
        // A new version went live while the page was open: show it.
        await consent.reload();
        setError(
          'The Terms of Use or Privacy Policy changed. Please read the current version and tick the box again.',
        );
        return;
      }
      // The API returns 200 with verification_sent even on duplicate
      // emails, so a real error here usually means a validation
      // problem (password too short, invalid email shape) — which
      // arrives as a Pydantic 422. extractErrorMessage handles both
      // the string-detail and the per-field array-detail shapes.
      setError(extractErrorMessage(err, 'registration failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Stack spacing={3}>
          <Typography variant="h4">Check your email</Typography>
          <Alert severity="success">
            If an account with that address didn't already exist, we've
            sent a verification link. Open the link to activate your
            account, then sign in.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Local dev: emails land in{' '}
            <MuiLink href="http://localhost:1080" target="_blank">
              maildev
            </MuiLink>
            .
          </Typography>
          <MuiLink component={Link} to="/login">
            Back to sign in
          </MuiLink>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3}>
        <Typography variant="h4">Create your account</Typography>
        <Box component="form" onSubmit={handleSubmit} autoComplete="on">
          <Stack spacing={2}>
            <TextField
              label="Full name"
              fullWidth
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              disabled={submitting}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
            />
            <Box>
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
                error={passwordError !== null}
              />
              <PasswordRulesHint value={password} />
            </Box>
            <LegalConsentCheckbox consent={consent} disabled={submitting} />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {submitting ? 'Creating…' : 'Create account'}
            </Button>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <MuiLink component={Link} to="/login">
            Sign in
          </MuiLink>
        </Typography>
      </Stack>
    </Container>
  );
}
